import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { DurationStats as DurationStatsType } from '../../types';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_CATEGORICAL,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface DurationStatsProps {
  stats: DurationStatsType;
  title?: string;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function DurationStats({ stats, title = 'Duration Analysis' }: DurationStatsProps) {
  const gradientId = useChartGradientId('duration-bars');

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-success-50 text-success-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    >

      {/* Summary Cards */}
      <div className="mb-3 grid flex-shrink-0 grid-cols-3 gap-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Average</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatDuration(stats.average)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Median</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatDuration(stats.median)}</p>
        </div>
        <div className="rounded-lg border border-danger-100 bg-danger-50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-danger-600">Maximum</p>
          <p className="mt-0.5 text-sm font-semibold text-danger-700">{formatDuration(stats.max)}</p>
        </div>
      </div>

      {/* Histogram */}
      {stats.histogram.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.histogram} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <defs>
                <ChartBarGradient id={gradientId} color={CHART_CATEGORICAL[0]} />
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="range"
                tick={CHART_AXIS_TICK}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={40}
              />
              <YAxis {...CHART_AXIS_PROPS} tick={CHART_AXIS_TICK} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number) => [`${value} scans`, 'Count']}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="count"
                fill={chartGradientFill(gradientId)}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>No duration data available</ChartEmptyState>
      )}
    </ChartPanel>
  );
}
